import { FC, useEffect, useRef, useState } from "react";
import {
  select,
  scaleBand,
  scaleLinear,
  max,
  scaleOrdinal,
  axisBottom,
  axisLeft,
} from "d3";
import { Box, Typography, TypographyProps } from "@mui/material";
import EmptyComponent from "../EmptyComponent";
import { getChartColors } from "../../helpers/getChartColors";
import CustomTooltip from "../CustomTooltip";

const colors = getChartColors();

export enum BAR_GRAPH_DIRECTION {
  HORIZONTAL,
  VERTICAL,
}

interface BarChartProps {
  data: Array<{ label: string; value: number;[key: string]: any }>;
  width?: string | number;
  height?: string | number;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showLabels?: boolean;
  axisFontSize?: number;
  labelFontSize?: number;
  categoryFontSize?: number;
  title?: string;
  tooltipTitle?: string;
  titleProps?: TypographyProps;
  direction?: BAR_GRAPH_DIRECTION;
  categoryPosition?: "TOP" | "BOTTOM";
  valueKey?: string;
  labelColNumber?: number;
  showAverageLine?: boolean;
  fromOTByOT?: boolean;
  showLabelInHeader?: boolean;
}

// Constants
const DEFAULT_VERTICAL_MARGINS = {
  left: 30,
  right: 0,
  top: 20,
  bottom: 50,
};
const DEFAULT_HORIZONTAL_MARGINS = {
  left: 50,
  right: 20,
  top: 0,
  bottom: 30,
};

// Define the number of grid lines
const numGridLines = 5;

// Helper functions
interface MarginOptions {
  isHorizontal: boolean;
  data: Array<{ label: string; value: number;[key: string]: any }>;
  showXAxis: boolean;
  showYAxis: boolean;
  isRotateXLabels: boolean;
  axisFontSize: number;
  valueKey?: string;
  fromOTByOT?: boolean;
}

// Helper for OTByOT left margin calculation
function getOTByOTLeftMargin(maxValue: number, isHorizontal: boolean, labelWidth: number): number {
  // Treat decimal values like 3.5 as two-digit numbers for margin
  const digits = maxValue.toString().length;
  let left: number;
  if (maxValue < 10) {
    left = 30;
  } else if (digits <= 2) {
    left = 25;
  } else {
    left = 25 + (digits - 5) * 10; // for even larger numbers
  }
  return isHorizontal ? Math.max(left, labelWidth) : left;
}

const calculateDynamicMargins = ({
  isHorizontal,
  data,
  showXAxis,
  showYAxis,
  isRotateXLabels,
  axisFontSize,
  valueKey = "value",
  fromOTByOT,
}: MarginOptions) => {
  const maxValue = max(data, (d) => d[valueKey]) ?? 0;
  const valueWidth = Math.max(maxValue.toString().length * axisFontSize, 40);
  const maxLabelWidth = Math.max(
    ...data.map((d) => d.label.length * axisFontSize * 0.75)
  );

  let labelWidth = 0;
  if (isHorizontal) {
    labelWidth = maxLabelWidth * 0.65;
  } else if (isRotateXLabels) {
    labelWidth = maxLabelWidth * 0.5;
  } else {
    labelWidth = 2.3 * axisFontSize;
  }

  let left: number;
  if (fromOTByOT) {
    left = getOTByOTLeftMargin(maxValue, isHorizontal, labelWidth);
  } else {
    left = isHorizontal ? labelWidth : valueWidth;
  }

  const bottom = !isHorizontal ? labelWidth : axisFontSize * 2;

  return {
    left: showYAxis ? left : 5,
    right: isHorizontal ? 20 : 0,
    top: isHorizontal ? 0 : 20,
    bottom: showXAxis ? bottom : 5,
  };
};

const shouldRotateXLabels = (
  data: Array<{ label: string; value: number;[key: string]: any }>,
  dimensions: { width: number; height: number },
  yLabelWidth: number,
  axisFontSize: number
) => {
  const barWidth = (dimensions.width - yLabelWidth) / data.length;
  const maxXLabelWidth = Math.max(
    ...data.map((d) => d.label.length * axisFontSize * 0.5)
  );
  return barWidth < maxXLabelWidth;
};

const drawHorizontalChart = (
  svg: any,
  data: Array<{ label: string; value: number;[key: string]: any }>,
  dimensions: { width: number; height: number },
  margins: typeof DEFAULT_VERTICAL_MARGINS,
  options: {
    showXAxis: boolean;
    showYAxis: boolean;
    showLabels: boolean;
    axisFontSize: number;
    labelFontSize: number;
  },
  valueKey: string = "value"
) => {
  const { showXAxis, showYAxis, showLabels, axisFontSize, labelFontSize } =
    options;
  svg.selectAll("*").remove();

  const innerWidth = dimensions.width - margins.left - margins.right;
  const innerHeight = dimensions.height - margins.top - margins.bottom;

  const xScale = scaleLinear()
    .domain([0, max(data, (d) => d[valueKey]) ?? 0])
    .nice()
    .range([0, innerWidth]);

  const yScale = scaleBand()
    .domain(data.map((d) => d.label))
    .range([0, innerHeight])
    .padding(0.4);

  const colorScale = scaleOrdinal()
    .domain(data.map((d) => d.label))
    .range(colors);

  const chart = svg
    .append("g")
    .attr("transform", `translate(${margins.left}, ${margins.top})`);

  // Generate grid line values manually
  const xTickValues = Array.from(
    { length: numGridLines },
    (_, i) =>
      (i / (numGridLines - 1)) * (xScale.domain()[1] - xScale.domain()[0])
  );

  // Draw grid lines
  chart
    .append("g")
    .attr("class", "grid")
    .selectAll("line")
    .data(xTickValues)
    .enter()
    .append("line")
    .attr("x1", (d: any) => xScale(d))
    .attr("x2", (d: any) => xScale(d))
    .attr("y1", (d: any) => 0)
    .attr("y2", (d: any) => innerHeight)
    .attr("stroke", "#e0e0e0")
    .attr("stroke-width", 2);

  // Draw bars
  chart
    .selectAll(".bar")
    .data(data)
    .enter()
    .append("rect")
    .attr("class", "bar")
    .attr("x", 0)
    .attr("y", (d: any) => yScale(d.label))
    .attr("width", (d: any) => xScale(d[valueKey]))
    .attr("height", yScale.bandwidth())
    .attr("fill", (d: any) => colorScale(d.label))
    .on("mouseover", function (event, d: any) {
      select(this.parentNode).select(".hover-bg").attr("fill", "#F2F1FF");
      showBarTooltip(chart, xScale, yScale, valueKey, d, "horizontal");
    })
    .on("mouseout", function () {
      select(this.parentNode).select(".hover-bg").attr("fill", "transparent");
      chart.selectAll(".bar-tooltip").remove();
    });

  // Labels on Bar
  if (showLabels) {
    chart
      .selectAll(".label")
      .data(data)
      .enter()
      .append("text")
      .attr("x", (d: any) => (xScale(d[valueKey]) ?? 0) + 5)
      .attr("y", (d: any) => (yScale(d.label) ?? 0) + yScale.bandwidth() / 2)
      .attr("text-anchor", "start")
      .attr("fill", "black")
      .style("font-size", labelFontSize)
      .text((d: any) => d[valueKey]);
  }

  // Y-Axis
  if (showYAxis) {
    chart
      .append("g")
      .call(axisLeft(yScale))
      .call((g: any) => g.select(".domain").remove())
      .call((g: any) => g.selectAll("line").remove())
      .selectAll("text")
      .attr("fill", "rgba(178, 178, 178, 1)")
      .style("font-size", axisFontSize);
  }

  // X-Axis
  if (showXAxis) {
    chart
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(axisBottom(xScale).tickValues(xTickValues))
      .call((g: any) => g.select(".domain").remove())
      .call((g: any) => g.selectAll("line").remove())
      .selectAll("text")
      .attr("fill", "rgba(178, 178, 178, 1)")
      .style("font-size", axisFontSize);
  }

  return { chart, xScale, yScale };
};

// Helper: Draw average line
function drawAverageLine(
  chart: any,
  data: Array<{ label: string; value: number;[key: string]: any }>,
  yScale: any,
  innerWidth: number,
  valueKey: string
) {
  const values = data.map((d: { [key: string]: any }) => d[valueKey]).filter((v: number) => typeof v === 'number');
  const avgValue = values.length > 0 ? values.reduce((a: number, b: number) => a + b, 0) / values.length : 0;
  if (avgValue > 0) {
    const avgY = yScale(avgValue);
    if (avgY !== undefined) {
      chart
        .append("line")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", avgY)
        .attr("y2", avgY)
        .attr("stroke", "#B67B0E")
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "8,4"); // dashed line
    }
  }
}

// Helper: Draw bar labels
function drawBarLabels(
  chart: any,
  data: Array<{ label: string; value: number;[key: string]: any }>,
  xScale: any,
  yScale: any,
  labelFontSize: number,
  valueKey: string
) {
  chart
    .selectAll(".label")
    .data(data)
    .enter()
    .append("text")
    .attr("x", (d: { label: string }) => (xScale(d.label) ?? 0) + xScale.bandwidth() / 2)
    .attr("y", (d: { [key: string]: any }) => (yScale(d[valueKey]) ?? 0) - 5)
    .attr("text-anchor", "middle")
    .attr("fill", "black")
    .style("font-size", labelFontSize)
    .text((d: { [key: string]: any }) => Math.round(d[valueKey]));
}

// Helper: Draw Y Axis
function drawYAxis(
  chart: any,
  yScale: any,
  yTickValues: number[],
  axisFontSize: number
) {
  chart
    .append("g")
    .call(axisLeft(yScale).tickValues(yTickValues))
    .call((g: any) => g.select(".domain").remove())
    .call((g: any) => g.selectAll("line").remove())
    .selectAll("text")
    .attr("fill", "rgba(178, 178, 178, 1)")
    .style("font-size", axisFontSize)
    .call((g: any) => g.select(".domain").remove())
    .call((g: any) => g.selectAll("line").remove())
    .selectAll("text")
    .attr("fill", "rgba(178, 178, 178, 1)")
    .style("font-size", axisFontSize);
}

// Helper: Draw X Axis
function drawXAxis(
  chart: any,
  xScale: any,
  innerHeight: number,
  axisFontSize: number,
  isRotateXLabels: boolean
) {
  chart
    .append("g")
    .attr("transform", `translate(0,${innerHeight})`)
    .call(axisBottom(xScale))
    .call((g: any) => g.select(".domain").remove())
    .call((g: any) => g.selectAll("line").remove())
    .call((g: any) => g.select(".domain").remove())
    .call((g: any) => g.selectAll("line").remove())
    .selectAll("text")
    .attr("text-anchor", isRotateXLabels ? "end" : "middle")
    .attr("fill", "rgba(178, 178, 178, 1)")
    .attr("transform", isRotateXLabels ? "rotate(-45)" : "rotate(0)")
    .attr("dx", isRotateXLabels ? "-0.8em" : "0")
    .attr("dy", isRotateXLabels ? "-0.2em" : "1em")
    .style("font-size", axisFontSize);
}

// Tooltip label
function showBarTooltip(
  chart: any,
  xScale: any,
  yScale: any,
  valueKey: any,
  d: any,
  orientation: "vertical" | "horizontal" = "vertical") {
  chart.selectAll(".bar-tooltip").remove();

  const tooltipGroup = chart
    .append("g")
    .attr("class", "bar-tooltip")
    .style("pointer-events", "none");

  const value = d[valueKey];

  let xPos = 0;
  let yPos = 0;
  if (orientation === "vertical") {
    const width = xScale(d.label) ?? 0;
    xPos = (width) + xScale.bandwidth() / 2;
    yPos = yScale(value) - 16;
  } else {
    xPos = xScale(value) + 20;
    yPos = (yScale(d.label) ?? 0) + yScale.bandwidth() / 2;
  }

  const tooltipContent = tooltipGroup
    .append("g")
    .attr("transform", `translate(${xPos},${yPos})`);

  const text = tooltipContent.append("text")
    .style("font-size", "12px")
    .style("fill", "#fff")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "middle")
    .text(`${value}`);

  const bbox = (text.node() as SVGTextElement).getBBox();

  const arrowSize = 6; // size of the triangle
  const arrowX = 0;    // center horizontally
  const arrowY = bbox.height / 2; // adjust vertical position

  if (orientation === "vertical") {
    tooltipContent.append("polygon")
      .attr("points", `
      ${arrowX - arrowSize},${arrowY} 
      ${arrowX + arrowSize},${arrowY} 
      ${arrowX},${arrowY + arrowSize}
    `)
      .style("fill", "#1a1832");
  } else {
    const arrowLeftX = -(bbox.width / 2) - 4.5;
    const arrowLeftY = -1;
    tooltipContent
      .append("polygon")
      .attr(
        "points",
        `
        ${arrowLeftX},${arrowLeftY - arrowSize}
        ${arrowLeftX},${arrowLeftY + arrowSize}
        ${arrowLeftX - arrowSize},${arrowLeftY}
      `
      )
      .style("fill", "#1a1832");
  }

  tooltipContent
    .insert("rect", "text")
    .attr("x", bbox.x - 5)
    .attr("y", bbox.y - 3)
    .attr("width", bbox.width + 10)
    .attr("height", bbox.height + 6)
    .attr("rx", 5)
    .attr("ry", 5)
    .style("fill", "#1a1832")
    .style("opacity", 0.95);
}

const drawVerticalChart = (
  svg: any,
  data: Array<{ label: string; value: number;[key: string]: any }>,
  dimensions: { width: number; height: number },
  margins: typeof DEFAULT_VERTICAL_MARGINS,
  options: {
    showXAxis: boolean;
    showYAxis: boolean;
    showLabels: boolean;
    isRotateXLabels: boolean;
    axisFontSize: number;
    labelFontSize: number;
    showAverageLine?: boolean;
  },
  valueKey: string = "value"
) => {
  const {
    showXAxis,
    showYAxis,
    showLabels,
    isRotateXLabels,
    axisFontSize,
    labelFontSize,
    showAverageLine,
  } = options;
  svg.selectAll("*").remove();

  const innerWidth = dimensions.width - margins.left - margins.right;
  const innerHeight = dimensions.height - margins.top - margins.bottom;

  const xScale = scaleBand()
    .domain(data.map((d) => d.label))
    .range([0, innerWidth])
    .padding(0.4);

  const yScale = scaleLinear()
    .domain([0, max(data, (d) => d[valueKey]) * 1.1])
    .nice()
    .range([innerHeight, 0]);

  const colorScale = scaleOrdinal()
    .domain(data.map((d) => d.label))
    .range(colors);

  const chart = svg
    .append("g")
    .attr("transform", `translate(${margins.left}, ${margins.top})`);

  // Generate grid line values manually
  const yTickValues = Array.from(
    { length: numGridLines },
    (_, i) =>
      (i / (numGridLines - 1)) * (yScale.domain()[1] - yScale.domain()[0])
  );

  // Draw grid lines
  chart
    .append("g")
    .attr("class", "grid")
    .selectAll("line")
    .data(yTickValues)
    .enter()
    .append("line")
    .attr("x1", 0)
    .attr("x2", innerWidth)
    .attr("y1", (d: any) => yScale(d))
    .attr("y2", (d: any) => yScale(d))
    .attr("stroke", "#e0e0e0")
    .attr("stroke-width", 2);

  // Draw bars
  chart
    .selectAll(".bar")
    .data(data)
    .enter()
    .append("rect")
    .attr("class", "bar")
    .attr("x", (d: any) => xScale(d.label) ?? "")
    .attr("y", (d: any) => yScale(d[valueKey]))
    .attr("width", xScale.bandwidth())
    .attr("height", (d: any) => innerHeight - yScale(d[valueKey]))
    .attr("fill", (d: any) => colorScale(d.label))
    .on("mouseover", function (event, d: any) {
      select(this.parentNode).select(".hover-bg").attr("fill", "#F2F1FF");
      showBarTooltip(chart, xScale, yScale, valueKey, d, "vertical");
    })
    .on("mouseout", function () {
      select(this.parentNode).select(".hover-bg").attr("fill", "transparent");
      chart.selectAll(".bar-tooltip").remove();
    });


  // Draw average line if enabled
  if (showAverageLine) {
    drawAverageLine(chart, data, yScale, innerWidth, valueKey);
  }

  // Labels on Bar
  if (showLabels) {
    drawBarLabels(chart, data, xScale, yScale, labelFontSize, valueKey);
  }

  // Y-Axis
  if (showYAxis) {
    drawYAxis(chart, yScale, yTickValues, axisFontSize);
  }

  // X-Axis
  if (showXAxis) {
    drawXAxis(chart, xScale, innerHeight, axisFontSize, isRotateXLabels);
  }

  return { chart, xScale, yScale };
};
interface LegendRowProps {
  data: Array<{ label: string }>;
  colors: string[];
  fontSize?: number;
  minWidth?: number;
  onHover?: (label: string) => void;
  onLeave?: () => void;
}
const LegendRow = ({ data, colors, fontSize = 14, minWidth = 50, onHover, onLeave }: LegendRowProps) => (
  <Box sx={{
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    gap: 2,
    rowGap: 0.5,
    columnGap: 2,
    alignItems: "flex-start",
    mb: 2,
  }}>
    {data?.map(({ label }, index: number) => (
      <Box 
        key={label} 
        onMouseEnter={() => onHover?.(label)}
        onMouseLeave={() => onLeave?.()}
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth,
          mb: 0.5,
          mr: 2,
          cursor: "pointer",
        }}
      >
        <Box sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          mr: 1,
          backgroundColor: colors[index],
        }} />
        <Typography fontSize={fontSize} noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
);

const BarChart: FC<BarChartProps> = ({
  data,
  width = "100%",
  height = "100%",
  showXAxis = true,
  showYAxis = true,
  showLabels = false,
  axisFontSize = 14,
  labelFontSize = 12,
  categoryFontSize = 14,
  title,
  tooltipTitle,
  titleProps,
  direction = BAR_GRAPH_DIRECTION.VERTICAL,
  categoryPosition,
  valueKey = "value",
  labelColNumber = 3,
  showAverageLine = false,
  showLabelInHeader = true,
  fromOTByOT,
}) => {
  const isHorizontal = direction === BAR_GRAPH_DIRECTION.HORIZONTAL;
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef();
  const chartContextRef = useRef<any>(null);
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [isRotateXLabels, setIsRotateXLabels] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [margins, setMargins] = useState(
    isHorizontal ? DEFAULT_HORIZONTAL_MARGINS : DEFAULT_VERTICAL_MARGINS
  );

  useEffect(() => {
    // to calculate the dimensions on graph according to parent
    // to calculate the dimensions on graph according to parent
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setDimensions({ width, height });
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [data?.length]);

  useEffect(() => {
    // For settings the margins of graph
    if (!data?.length || !dimensions?.width || !dimensions?.height) {
      return;
    }

    if (!isHorizontal) {
      const maxYValue = max(data, (d) => d[valueKey]) ?? 0;
      const yLabelWidth = maxYValue.toString().length * axisFontSize;
      const rotateXLabels = shouldRotateXLabels(
        data,
        dimensions,
        yLabelWidth,
        axisFontSize
      );
      setIsRotateXLabels(rotateXLabels);
    }

    const newMargins = calculateDynamicMargins({
      isHorizontal,
      data,
      showXAxis,
      showYAxis,
      isRotateXLabels,
      axisFontSize,
      valueKey,
      fromOTByOT,
    });
    setMargins(newMargins);
  }, [
    axisFontSize,
    data,
    dimensions,
    direction,
    isHorizontal,
    isRotateXLabels,
    showXAxis,
    showYAxis,
    valueKey,
    fromOTByOT,
  ]);

  useEffect(() => {
    if (!data?.length || !dimensions?.width || !dimensions?.height) return;

    const svg = select(svgRef.current);

    if (isHorizontal) {
      chartContextRef.current = drawHorizontalChart(
        svg,
        data,
        dimensions,
        margins,
        {
          showXAxis,
          showYAxis,
          showLabels,
          axisFontSize,
          labelFontSize,
        },
        valueKey
      );
    } else {
      chartContextRef.current = drawVerticalChart(
        svg,
        data,
        dimensions,
        margins,
        {
          showXAxis,
          showYAxis,
          showLabels,
          isRotateXLabels,
          axisFontSize,
          labelFontSize,
          showAverageLine,
        },
        valueKey
      );
    }
  }, [
    data,
    dimensions,
    margins,
    showXAxis,
    showYAxis,
    showLabels,
    axisFontSize,
    labelFontSize,
    isHorizontal,
    isRotateXLabels,
    valueKey,
    showAverageLine,
  ]);

  useEffect(() => {
    if (!chartContextRef.current) return;
    const { chart, xScale, yScale } = chartContextRef.current;

    if (hoveredLabel) {
      const d = data.find((item) => item.label === hoveredLabel);
      if (d) {
        showBarTooltip(chart, xScale, yScale, valueKey, d, isHorizontal ? "horizontal" : "vertical");
        chart.select(".hover-bg").attr("fill", "#F2F1FF");
      }
    } else {
      chart.selectAll(".bar-tooltip").remove();
      chart.select(".hover-bg").attr("fill", "transparent");
    }
  }, [hoveredLabel, isHorizontal, valueKey, data]);

  return (
    <Box flex={1} display="flex" flexDirection="column" sx={{ height: "100%" }}>
      {title && (
        <CustomTooltip title={tooltipTitle}>
          <Typography
            variant="h6"
            color="custom.foreground.secondary"
            mb={3}
            sx={titleProps}
          >
            {title}
          </Typography>
        </CustomTooltip>
      )}
      {data?.length > 0 && !data.every((item) => item[valueKey] === 0) ? (
        <Box
          flex={1}
          display="flex"
          flexDirection={
            categoryPosition === "BOTTOM" ? "column-reverse" : "column"
          }
          gap={3}
        >
          {showLabelInHeader && (
            <LegendRow
              data={data}
              colors={colors}
              fontSize={categoryFontSize}
              minWidth={50}
              onHover={setHoveredLabel}
              onLeave={() => setHoveredLabel(null)}
            />
          )}
          <Box ref={containerRef} flex={1} sx={{ width, height }}>
            <svg ref={svgRef} style={{ width: "100%", height: "100%" }}></svg>
          </Box>
        </Box>
      ) : (
        <EmptyComponent />
      )}
    </Box>
  );
};

export default BarChart;
